import { useState } from 'react'
import { motion } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Checkbox } from '@/components/ui/checkbox'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { 
  User, 
  MapPin, 
  Briefcase,
  UserCheck,
  AlertCircle,
  HelpCircle,
  CheckCircle,
  XCircle,
  FileText,
  Users,
  GraduationCap,
  Mail,
  Hash,
  Send
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { FOProfileData, HRDataCorrectionTicket } from '@/types/onboarding'
import { CORRECTABLE_FIELDS } from '@/data/correctableFields'

interface FOConfirmationStepProps {
  foData: FOProfileData
  onConfirm: () => void
  onReportIncorrect: (issue: string) => void
  onBack: () => void
}

// Pure CSS tooltip — hover-only, no sticky behavior
function FieldTooltip({ text }: { text: string }) {
  return (
    <span className="relative inline-flex ml-1 group/tip cursor-help">
      <HelpCircle className="h-3.5 w-3.5 text-muted-foreground/60 group-hover/tip:text-muted-foreground transition-colors" />
      <span className="pointer-events-none absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[260px] rounded-lg bg-gray-900 px-3.5 py-2.5 text-xs leading-relaxed text-gray-100 shadow-lg opacity-0 scale-95 transition-all duration-150 group-hover/tip:opacity-100 group-hover/tip:scale-100 z-50">
        {text}
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 -mb-px border-4 border-transparent border-b-gray-900" />
      </span>
    </span>
  )
}

export default function FOConfirmationStep({
  foData,
  onConfirm,
  onReportIncorrect,
  onBack
}: FOConfirmationStepProps) {
  const [, setIsDataCorrect] = useState<boolean | null>(null)
  const [reportingIssue, setReportingIssue] = useState(false)
  const [hrTicketCreated, setHrTicketCreated] = useState(false)
  
  // Form state for correction request
  const [selectedFields, setSelectedFields] = useState<string[]>([])
  const [issueDescription, setIssueDescription] = useState('')

  // Augment shared field list with current F&O values for reference
  const currentValues: Record<string, string | undefined> = {
    name: foData.displayName || `${foData.firstName} ${foData.lastName}`,
    personnelNumber: foData.employeeId,
    roleTitle: foData.jobTitle,
    email: foData.email,
    function: foData.function || foData.functionalArea || 'Not specified',
    lineManager: foData.lineManagerName || 'Not assigned',
    campus: foData.campus,
    faculty: foData.faculty || 'Not specified',
    managerStatus: foData.directReports > 0 ? `People Leader (${foData.directReports} reports)` : 'Not a People Leader',
    other: undefined,
  }
  const correctableFields = CORRECTABLE_FIELDS.map(f => ({
    ...f,
    currentValue: currentValues[f.id],
  }))

  const handleDataIncorrect = () => {
    setIsDataCorrect(false)
    setReportingIssue(true)
  }

  const handleFieldToggle = (fieldId: string) => {
    setSelectedFields(prev => 
      prev.includes(fieldId) 
        ? prev.filter(f => f !== fieldId)
        : [...prev, fieldId]
    )
  }

  const handleSubmitHRTicket = () => {
    // Create detailed HR ticket
    const ticket: HRDataCorrectionTicket = {
      ticketId: `HR-${Date.now()}`,
      employeeUserId: foData.userId,  // Database user ID for backend linking
      employeeId: foData.employeeId,
      employeeName: foData.displayName || `${foData.firstName} ${foData.lastName}`,
      issueType: 'profile-data',
      affectedFields: selectedFields,
      userDescription: issueDescription,
      source: 'fo-confirmation',
      description: `Team member reports incorrect F&O profile data. Affected fields: ${selectedFields.map(f => correctableFields.find(cf => cf.id === f)?.label).join(', ')}. Details: ${issueDescription || 'No additional details provided.'}`,
      status: 'pending',
      createdAt: new Date().toISOString()
    }
    
    onReportIncorrect(JSON.stringify(ticket))
    setHrTicketCreated(true)
  }

  const canSubmitTicket = selectedFields.length > 0 && issueDescription.trim().length > 0

  const handleDataCorrect = () => {
    setIsDataCorrect(true)
    setTimeout(() => onConfirm(), 500)
  }

  // Fields aligned with F&O confirmation requirements
  // Note: Some fields may not be available in F&O (marked with mayNotBeInFO)
  const profileFields: { icon: LucideIcon; label: string; value: string; category: string; mayNotBeInFO?: boolean }[] = [
    // Identity Section
    {
      icon: User,
      label: 'Name and Surname',
      value: foData.displayName || `${foData.firstName} ${foData.lastName}`,
      category: 'identity'
    },
    {
      icon: Hash,
      label: 'Personnel Number',
      value: foData.employeeId,
      category: 'identity'
    },
    {
      icon: Briefcase,
      label: 'Role Title',
      value: foData.jobTitle,
      category: 'identity'
    },
    {
      icon: Mail,
      label: 'Email',
      value: foData.email,
      category: 'identity'
    },
    // Organization Section - Function IS in F&O
    {
      icon: Briefcase,
      label: 'Function',
      value: foData.function || foData.functionalArea || 'Not specified',
      category: 'organization'
    },
    {
      icon: UserCheck,
      label: 'People Leader',
      value: foData.lineManagerName || 'Not assigned',
      category: 'organization'
    },
    {
      icon: MapPin,
      label: 'Campus',
      value: foData.campus,
      category: 'organization'
    },
    {
      icon: GraduationCap,
      label: 'Faculty',
      value: foData.faculty || 'Not specified',
      category: 'organization'
    },
    // Manager Status Section
    {
      icon: Users,
      label: 'People Leader Status',
      value: foData.directReports > 0 ? `People Leader (${foData.directReports} direct report${foData.directReports > 1 ? 's' : ''})` : 'Not a People Leader (no direct reports)',
      category: 'manager'
    }
  ]

  if (hrTicketCreated) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center space-y-4"
        >
          <div className="mx-auto w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center">
            <AlertCircle className="h-8 w-8 text-amber-600" />
          </div>
          
          <h2 className="text-2xl font-larken font-bold text-heritage-blue">
            HR Data Correction Request Submitted
          </h2>
          
          <p className="text-muted-foreground max-w-xl mx-auto">
            We've created a ticket for the HR team to review and correct your F&O profile data. 
            You'll receive an email notification once the corrections are complete.
          </p>
        </motion.div>

        <Alert className="border-amber-200 bg-amber-50">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-sm">
            <strong>What happens next?</strong>
            <ul className="mt-2 space-y-1 text-xs">
              <li>• HR will review your profile data in the F&O system</li>
              <li>• They'll update any incorrect information at the source</li>
              <li>• The platform will automatically re-sync your data</li>
              <li>• You'll be able to complete onboarding once corrections are made</li>
            </ul>
          </AlertDescription>
        </Alert>

        <div className="flex justify-center">
          <Button variant="outline" onClick={() => window.location.href = '/'}>
            Return to Dashboard
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center space-y-2"
      >
        <h2 className="text-3xl font-larken font-bold text-heritage-blue inline-flex items-center justify-center gap-1">
          Let's Confirm Your Details
          <FieldTooltip text="This data comes from the Dynamics F&O system. If you notice any discrepancies, please report them so HR can update the source records." />
        </h2>
        <p className="text-muted-foreground">
          Please check that the information we have from F&O is correct
        </p>
      </motion.div>

      {/* Profile Data Display */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center space-x-2">
              <FileText className="h-5 w-5 text-heritage-blue" />
              <span>Team Member Information</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Identity & Employment Section */}
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Basic
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {profileFields.filter(f => f.category === 'identity').map((field, idx) => (
                  <div key={idx} className="flex items-start space-x-3 p-3 rounded-lg bg-muted/30">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 rounded-lg bg-heritage-blue/10 flex items-center justify-center">
                        <field.icon className="h-4 w-4 text-heritage-blue" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-muted-foreground">{field.label}</p>
                      <p className="text-sm font-medium truncate">{field.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Organization Structure Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  Org-related
                </h4>
                {profileFields.filter(f => f.category === 'organization' && f.mayNotBeInFO).length > 0 && (
                  <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded">
                    * Teams/Faculty not in F&O
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {profileFields.filter(f => f.category === 'organization').map((field, idx) => (
                  <div key={idx} className={`flex items-start space-x-3 p-3 rounded-lg ${
                    field.mayNotBeInFO ? 'bg-amber-50/50 border border-amber-100' : 'bg-muted/30'
                  }`}>
                    <div className="flex-shrink-0">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        field.mayNotBeInFO ? 'bg-amber-100' : 'bg-future-blue/10'
                      }`}>
                        <field.icon className={`h-4 w-4 ${
                          field.mayNotBeInFO ? 'text-amber-600' : 'text-future-blue'
                        }`} />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-muted-foreground">{field.label}</p>
                        {field.mayNotBeInFO && (
                          <span className="text-[10px] text-amber-600">*</span>
                        )}
                      </div>
                      <p className={`text-sm font-medium ${
                        field.value === 'Not specified' ? 'text-muted-foreground italic' : ''
                      }`}>{field.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* People Leader Status */}
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                People Leader Status
              </h4>
              <div className="grid grid-cols-1 gap-4">
                {profileFields.filter(f => f.category === 'manager').map((field, idx) => (
                  <div key={idx} className={`flex items-start space-x-3 p-3 rounded-lg ${
                    foData.directReports > 0 ? 'bg-purple-50 border border-purple-100' : 'bg-muted/30'
                  }`}>
                    <div className="flex-shrink-0">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        foData.directReports > 0 ? 'bg-purple-100' : 'bg-gray-100'
                      }`}>
                        <field.icon className={`h-4 w-4 ${
                          foData.directReports > 0 ? 'text-purple-700' : 'text-gray-500'
                        }`} />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-muted-foreground">{field.label}</p>
                      <p className="text-sm font-medium">{field.value}</p>
                      {!(foData.directReports > 0) && (
                        <p className="text-xs text-amber-600 mt-1">
                          💡 If you are a people leader with direct reports, please report this as incorrect so HR can update F&O.
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Confirmation Question */}
      {!reportingIssue && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="border-2 border-heritage-blue/20">
            <CardContent className="p-6">
              <div className="text-center space-y-4">
                <h3 className="text-lg font-semibold">Above information correct?</h3>
                
                <div className="flex justify-center space-x-4">
                  <Button
                    onClick={handleDataCorrect}
                    className="bg-green-600 hover:bg-green-700 text-white px-8"
                    size="lg"
                  >
                    Yes
                  </Button>
                  
                  <Button
                    onClick={handleDataIncorrect}
                    variant="destructive"
                    className="px-8"
                    size="lg"
                  >
                    No
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* HR Escalation Flow - Detailed Form */}
      {reportingIssue && !hrTicketCreated && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="border-2 border-amber-200 bg-amber-50/50">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
                <AlertCircle className="h-5 w-5" />
                What's incorrect? <span className="text-red-500">*</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Field Selection */}
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {correctableFields.map((field) => (
                    <div
                      key={field.id}
                      className={`flex items-start space-x-2 p-2.5 rounded-lg border transition-colors cursor-pointer ${
                        selectedFields.includes(field.id)
                          ? 'bg-amber-100 border-amber-400'
                          : 'bg-white border-gray-200 hover:border-amber-300'
                      }`}
                      onClick={() => handleFieldToggle(field.id)}
                    >
                      <Checkbox
                        id={field.id}
                        checked={selectedFields.includes(field.id)}
                        onCheckedChange={() => handleFieldToggle(field.id)}
                        className="data-[state=checked]:bg-amber-600 data-[state=checked]:border-amber-600 mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <Label
                          htmlFor={field.id}
                          className="text-sm cursor-pointer font-medium"
                        >
                          {field.label}
                        </Label>
                        {field.currentValue && (
                          <p className="text-xs text-muted-foreground mt-0.5 truncate">
                            Current: <span className="font-medium text-foreground/70">{field.currentValue}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="issueDescription" className="text-sm font-semibold text-amber-900">
                  Please describe the issue <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="issueDescription"
                  placeholder="e.g., My role title shows as 'Administrator' but should be 'Senior Administrator'. My people leader is listed incorrectly and should be: [Name]."
                  value={issueDescription}
                  onChange={(e) => setIssueDescription(e.target.value)}
                  className="min-h-[100px] bg-white"
                />
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-2 border-t border-amber-200">
                <Button
                  onClick={() => {
                    setReportingIssue(false)
                    setSelectedFields([])
                    setIssueDescription('')
                  }}
                  variant="outline"
                  size="sm"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmitHRTicket}
                  disabled={!canSubmitTicket}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  size="sm"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Submit
                </Button>
              </div>

              {!canSubmitTicket && (
                <p className="text-xs text-red-600 text-center">
                  {selectedFields.length === 0
                    ? 'Please select at least one field that needs correction'
                    : 'Please describe the issue'}
                </p>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <Button onClick={onBack} variant="outline">
          Back
        </Button>
      </div>
    </div>
  )
}
